import type { FeatureCollection, Feature, Geometry } from 'geojson';

/**
 * Valid GeoJSON geometry type strings.
 */
const VALID_GEOMETRY_TYPES = [
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
  'GeometryCollection',
] as const;

/**
 * Valid top-level GeoJSON object type strings.
 */
const VALID_GEOJSON_TYPES = ['FeatureCollection', 'Feature', ...VALID_GEOMETRY_TYPES] as const;

/**
 * Error thrown when GeoJSON parsing or validation fails.
 */
export class GeoJSONParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeoJSONParseError';
  }
}

/**
 * Validates that a value is a valid GeoJSON geometry object.
 * Checks for required `type` and `coordinates` fields (or `geometries` for GeometryCollection).
 */
function validateGeometry(geometry: unknown): geometry is Geometry {
  if (!geometry || typeof geometry !== 'object') {
    return false;
  }

  const geo = geometry as Record<string, unknown>;

  if (!geo.type || typeof geo.type !== 'string') {
    return false;
  }

  if (!VALID_GEOMETRY_TYPES.includes(geo.type as (typeof VALID_GEOMETRY_TYPES)[number])) {
    return false;
  }

  if (geo.type === 'GeometryCollection') {
    if (!Array.isArray(geo.geometries)) {
      return false;
    }
    return (geo.geometries as unknown[]).every(validateGeometry);
  }

  // All other geometry types require coordinates
  if (!Array.isArray(geo.coordinates)) {
    return false;
  }

  return true;
}

/**
 * Validates that a value is a valid GeoJSON Feature object.
 */
function validateFeature(feature: unknown): feature is Feature {
  if (!feature || typeof feature !== 'object') {
    return false;
  }

  const feat = feature as Record<string, unknown>;

  if (feat.type !== 'Feature') {
    return false;
  }

  // geometry can be null (valid per GeoJSON spec) or a valid geometry
  if (feat.geometry !== null && !validateGeometry(feat.geometry)) {
    return false;
  }

  // properties must be an object or null
  if (
    feat.properties !== null &&
    feat.properties !== undefined &&
    typeof feat.properties !== 'object'
  ) {
    return false;
  }

  return true;
}

/**
 * Validates that a value is a valid GeoJSON FeatureCollection.
 */
function validateFeatureCollection(data: unknown): data is FeatureCollection {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const fc = data as Record<string, unknown>;

  if (fc.type !== 'FeatureCollection') {
    return false;
  }

  if (!Array.isArray(fc.features)) {
    return false;
  }

  return (fc.features as unknown[]).every(validateFeature);
}

/**
 * Wraps a bare Geometry object into a FeatureCollection.
 */
function geometryToFeatureCollection(geometry: Geometry): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry,
        properties: {},
      },
    ],
  };
}

/**
 * Wraps a single Feature into a FeatureCollection.
 */
function featureToFeatureCollection(feature: Feature): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [
      {
        ...feature,
        properties: feature.properties ?? {},
      },
    ],
  };
}

/**
 * Parses and validates a GeoJSON string or ArrayBuffer, normalizing the result
 * to a FeatureCollection.
 *
 * Accepts:
 * - FeatureCollection (returned as-is after validation)
 * - Feature (wrapped in a FeatureCollection)
 * - Bare Geometry (wrapped in a Feature, then in a FeatureCollection)
 *
 * @param data - GeoJSON content as a string or ArrayBuffer
 * @returns A validated GeoJSON FeatureCollection
 * @throws GeoJSONParseError if the input is invalid
 */
export function toGeoJSON(data: string | ArrayBuffer): FeatureCollection {
  const text = decodeInput(data);
  const parsed = parseJSON(text);
  return normalizeGeoJSON(parsed);
}

/**
 * Decodes input data to a string.
 */
function decodeInput(data: string | ArrayBuffer): string {
  if (typeof data === 'string') {
    return data;
  }

  // Handle ArrayBuffer and typed array views (covers jsdom quirks with ArrayBuffer identity)
  if (typeof data === 'object' && data !== null && 'byteLength' in data) {
    try {
      const decoder = new TextDecoder('utf-8');
      return decoder.decode(data as ArrayBuffer);
    } catch {
      throw new GeoJSONParseError('Failed to decode ArrayBuffer as UTF-8 text');
    }
  }

  throw new GeoJSONParseError('Input must be a string or ArrayBuffer');
}

/**
 * Parses a JSON string into an object.
 */
function parseJSON(text: string): unknown {
  const trimmed = text.trim();

  if (!trimmed) {
    throw new GeoJSONParseError('Input is empty');
  }

  try {
    return JSON.parse(trimmed);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown parse error';
    throw new GeoJSONParseError(`Invalid JSON: ${message}`);
  }
}

/**
 * Normalizes a parsed GeoJSON object to a FeatureCollection.
 */
function normalizeGeoJSON(parsed: unknown): FeatureCollection {
  if (!parsed || typeof parsed !== 'object') {
    throw new GeoJSONParseError('GeoJSON must be a JSON object');
  }

  const obj = parsed as Record<string, unknown>;

  if (!obj.type || typeof obj.type !== 'string') {
    throw new GeoJSONParseError('GeoJSON object must have a "type" field');
  }

  if (!VALID_GEOJSON_TYPES.includes(obj.type as (typeof VALID_GEOJSON_TYPES)[number])) {
    throw new GeoJSONParseError(
      `Invalid GeoJSON type "${obj.type}". Expected one of: ${VALID_GEOJSON_TYPES.join(', ')}`
    );
  }

  // Handle FeatureCollection
  if (obj.type === 'FeatureCollection') {
    if (!validateFeatureCollection(parsed)) {
      throw new GeoJSONParseError(
        'Invalid FeatureCollection: "features" must be an array of valid Feature objects'
      );
    }
    // Ensure all features have properties object (normalize null to {})
    const fc = parsed as FeatureCollection;
    return {
      type: 'FeatureCollection',
      features: fc.features.map((f) => ({
        ...f,
        properties: f.properties ?? {},
      })),
    };
  }

  // Handle single Feature
  if (obj.type === 'Feature') {
    if (!validateFeature(parsed)) {
      throw new GeoJSONParseError(
        'Invalid Feature: must have a valid "geometry" field and optional "properties" object'
      );
    }
    return featureToFeatureCollection(parsed as Feature);
  }

  // Handle bare Geometry
  if (!validateGeometry(parsed)) {
    throw new GeoJSONParseError(
      `Invalid geometry of type "${obj.type}": must have valid "coordinates" or "geometries" field`
    );
  }
  return geometryToFeatureCollection(parsed as Geometry);
}
